import React, { useState, useMemo } from "react";
import { useT, Namespace, useApiErrorMessage } from "@racoon/i18n";
import { Button, useModal, useToast } from "@racoon/ui";
import { TruckTypesTable } from "./truck-types-table";
import { TruckTypeStatusFilter } from "./truck-type-status-filter";
import { TruckTypeDialog } from "./truck-type-dialog";
import {
  useListTruckTypesQuery,
  useDeleteTruckTypeMutation,
} from "../../../../shared/api/truck/truck-type.api";
import { useSelectedCompany } from "../../../../shared/store/company/hooks/useSelectedCompany";
import { ConnectProto, type ApiError } from "@racoon/api";
import { useCanWriteType } from "~/shared/hooks/use-can-write-type";

import { normalizeForSearch } from "@racoon/lib";
type CustomTruckType = ConnectProto.CustomTruckType;

interface TruckTypesSettingsProps {
  selectedCompanyId?: number;
}

export const TruckTypesSettings: React.FC<TruckTypesSettingsProps> = ({
  selectedCompanyId: propSelectedCompanyId,
}) => {
  const t = useT();
  const { toast } = useToast();
  const describeError = useApiErrorMessage();
  const { openModal, closeModal } = useModal();
  const { selectedCompanyId: storeSelectedCompanyId } = useSelectedCompany();
  const selectedCompanyId = propSelectedCompanyId ?? storeSelectedCompanyId;
  const canWrite = useCanWriteType("truck", selectedCompanyId);

  const [selectedType, setSelectedType] = useState<CustomTruckType | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState<CustomTruckType | null>(null);

  const [deleteTruckType] = useDeleteTruckTypeMutation();

  const { data: truckTypesResponse, isLoading } = useListTruckTypesQuery(
    { ownerOrganizationIds: selectedCompanyId ? [BigInt(selectedCompanyId)] : [] },
    { skip: !selectedCompanyId },
  );

  const truckTypes = truckTypesResponse?.customTruckTypes || [];

  // Filter data by status and search
  const filteredTypes = useMemo(() => {
    let filtered = truckTypes;

    // Filter by search
    if (searchValue.trim()) {
      const searchLower = normalizeForSearch(searchValue);
      filtered = filtered.filter(
        (type) =>
          normalizeForSearch(type.manufacturer).includes(searchLower) ||
          normalizeForSearch(type.model).includes(searchLower),
      );
    }

    return filtered;
  }, [truckTypes, searchValue]);

  const handleRowSelect = (type: CustomTruckType | null) => {
    setSelectedType(type);
  };

  const handleCreateClick = () => {
    setEditingType(null);
    setDialogOpen(true);
  };

  const handleEditClick = () => {
    if (selectedType) {
      setEditingType(selectedType);
      setDialogOpen(true);
    }
  };

  const handleConfirmDelete = async (type: CustomTruckType) => {
    closeModal();
    try {
      await deleteTruckType({
        id: type.id,
        ownerOrganizationId: type.ownerOrganizationId,
      }).unwrap();
      toast({ title: t(Namespace.Settings, "truckType.dialog.deleteSuccess") });
      setSelectedType(null);
    } catch (error) {
      toast({
        title: t(Namespace.Settings, "truckType.dialog.error"),
        // e.g. trucks still use it: the message says how many
        description: describeError(error as ApiError),
        variant: "destructive",
      });
    }
  };

  const handleDeleteClick = () => {
    if (!selectedType) return;
    const typeToDelete = selectedType;

    openModal({
      title: t(Namespace.Settings, "truckType.dialog.deleteConfirmTitle"),
      closeButtonTop: "top-4",
      body: (
        <p className="text-center text-text-primary text-sm">
          {t(Namespace.Settings, "truckType.dialog.deleteConfirmDescription", {
            name: typeToDelete.name,
          })}
        </p>
      ),
      size: "sm",
      footer: (
        <div className="flex w-full justify-end gap-2">
          <Button size="sm" variant="outline" onClick={() => closeModal()}>
            {t(Namespace.Buttons, "cancel")}
          </Button>
          <Button
            size="sm"
            variant="opacityRedWithOutline"
            onClick={() => handleConfirmDelete(typeToDelete)}
          >
            {t(Namespace.Buttons, "delete")}
          </Button>
        </div>
      ),
    });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <TruckTypeStatusFilter
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onCreateClick={handleCreateClick}
        // A type is deleted with a write right on its company; others open read only
        onEditClick={selectedType ? handleEditClick : undefined}
        onDeleteClick={selectedType && canWrite(selectedType) ? handleDeleteClick : undefined}
      />

      <TruckTypesTable
        truckTypes={filteredTypes}
        isLoading={isLoading}
        selectedType={selectedType}
        onRowSelect={handleRowSelect}
      />

      <TruckTypeDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        truckType={editingType}
        ownerOrganizationId={selectedCompanyId ?? null}
      />
    </div>
  );
};
